import React, { useState } from "react";
import { MessageCircle, ExternalLink, QrCode, ShieldAlert, Smartphone, Building2, AppWindow, Info } from "lucide-react";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { adminNav } from "../../components/shared/navConfig";

const WA_URL = "https://web.whatsapp.com/";
const STEPS = [
  "Click “Open WhatsApp Web” below. It opens in a separate window next to this panel.",
  "On your phone, open WhatsApp → Settings (or ⋮ menu) → Linked devices → Link a device.",
  "Point your phone camera at the QR code shown in the WhatsApp Web window.",
  "Keep that window open while you work. Your login stays inside WhatsApp, not in Assisto4U.",
];

export default function AdminWhatsApp() {
  const [status, setStatus] = useState("idle");
  const openWindow = () => {
    const w = window.open(WA_URL, "assisto_whatsapp", "popup=yes,width=1200,height=820,left=80,top=60");
    setStatus(w ? "opened" : "blocked");
  };
  return (
    <DashboardShell nav={adminNav} title="WhatsApp Center" subtitle="Chat with students and companies on WhatsApp Web">
      <div data-testid="wa-embed-notice" className="card-surface p-4 mb-5 flex gap-3 text-xs" style={{ color: "var(--text-muted)" }}>
        <ShieldAlert size={16} className="shrink-0 text-amber-500 mt-0.5"/>
        <div>
          <div className="font-medium mb-0.5" style={{ color: "var(--text)" }}>WhatsApp Web cannot be shown inside this page</div>
          WhatsApp's own security settings stop other websites from embedding it. So, instead of a fake copy, this center opens the real WhatsApp Web in its own window. Assisto4U never sees or stores your WhatsApp login.
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-5">
        <div className="card-surface p-5 sm:p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "#25D366" }}><MessageCircle size={20} color="#fff"/></div>
            <div><div className="font-display text-lg" style={{ color: "var(--text)" }}>WhatsApp Web workspace</div>
              <div data-testid="wa-status" className="text-xs" style={{ color: status === "blocked" ? "#F43F5E" : status === "opened" ? "#10B981" : "var(--text-dim)" }}>
                {status === "opened" ? "Window opened — scan the QR code there if asked" : status === "blocked" ? "Your browser blocked the pop-up. Allow pop-ups or use “Open in new tab”." : "Not opened yet"}
              </div></div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mb-6">
            <button data-testid="wa-open-window-btn" onClick={openWindow} className="hidden md:inline-flex btn-primary px-5 py-3 rounded-full text-sm items-center justify-center gap-2"><AppWindow size={15}/> Open WhatsApp Web</button>
            <a data-testid="wa-open-tab-btn" href={WA_URL} target="_blank" rel="noopener noreferrer" className="btn-ghost px-5 py-3 rounded-full text-sm inline-flex items-center justify-center gap-2"><ExternalLink size={15}/> Open in new tab</a>
            <a data-testid="wa-open-app-btn" href="whatsapp://send" className="md:hidden btn-primary px-5 py-3 rounded-full text-sm inline-flex items-center justify-center gap-2"><Smartphone size={15}/> Open WhatsApp app</a>
          </div>
          <div className="eyebrow mb-3 flex items-center gap-1.5"><QrCode size={12}/> Link your phone (QR login)</div>
          <ol data-testid="wa-steps" className="space-y-3">
            {STEPS.map((s, i) => (
              <li key={i} className="flex gap-3 text-sm" style={{ color: "var(--text-muted)" }}>
                <span className="w-6 h-6 shrink-0 rounded-full text-[11px] font-mono flex items-center justify-center border" style={{ borderColor: "var(--border-strong)", color: "var(--text)" }}>{i + 1}</span>{s}
              </li>
            ))}
          </ol>
          <p className="md:hidden text-xs mt-4 flex gap-2" style={{ color: "var(--text-dim)" }}><Info size={13} className="shrink-0 mt-0.5"/>On a phone, WhatsApp Web is not available — use the WhatsApp app directly.</p>
        </div>

        <div className="card-surface p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-2"><Building2 size={16} className="text-blue-500"/><div className="font-display" style={{ color: "var(--text)" }}>Automated business messages</div></div>
          <div data-testid="wa-business-status" className="chip mb-3" style={{ color: "var(--text-dim)" }}>Not connected</div>
          <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>
            To send automatic WhatsApp alerts (task allocated, KYC approved, payment due) straight from Assisto4U, connect the official WhatsApp Business Platform.
          </p>
          <ul className="text-xs space-y-1.5 list-disc pl-4" style={{ color: "var(--text-dim)" }}>
            <li>Needs a Meta Business account and a verified business phone number</li>
            <li>Message templates must be approved by Meta</li>
            <li>Meta charges per conversation</li>
          </ul>
          <p className="text-xs mt-4" style={{ color: "var(--text-dim)" }}>Ask your developer to connect it when you're ready. Nothing is sent automatically until then.</p>
        </div>
      </div>
    </DashboardShell>
  );
}
